/**
 * usePullToRefresh Hook
 *
 * Implements pull-to-refresh gesture for mobile web apps.
 * Triggers callback when user pulls down on a scrollable container.
 *
 * @example
 * ```tsx
 * const { pullHandlers, isPulling, pullDistance } = usePullToRefresh({
 *   onRefresh: async () => {
 *     await fetchData();
 *   },
 *   refreshThreshold: 80,
 * });
 *
 * return (
 *   <div {...pullHandlers} className="overflow-y-auto">
 *     {isPulling && <div>Pull to refresh ({pullDistance}px)</div>}
 *     <YourContent />
 *   </div>
 * );
 * ```
 */

import { useRef, useState, TouchEvent } from 'react';

/**
 * Pull-to-refresh configuration
 */
export interface PullToRefreshConfig {
  /** Callback triggered when refresh is initiated */
  onRefresh: () => Promise<void> | void;
  /** Distance threshold to trigger refresh (default: 80px) */
  refreshThreshold?: number;
  /** Maximum pull distance (default: 150px) */
  maxPullDistance?: number;
  /** Enable pull-to-refresh only when scrolled to top (default: true) */
  requireScrollTop?: boolean;
  /** Disabled state (default: false) */
  disabled?: boolean;
}

/**
 * Pull-to-refresh state
 */
export interface PullToRefreshState {
  /** Touch event handlers */
  pullHandlers: {
    onTouchStart: (e: TouchEvent) => void;
    onTouchMove: (e: TouchEvent) => void;
    onTouchEnd: (e: TouchEvent) => void;
  };
  /** Whether user is actively pulling */
  isPulling: boolean;
  /** Current pull distance in pixels */
  pullDistance: number;
  /** Whether refresh is in progress */
  isRefreshing: boolean;
  /** Whether pull threshold has been exceeded */
  canRefresh: boolean;
}

/**
 * Touch position interface
 */
interface TouchPosition {
  y: number;
  scrollTop: number;
}

/**
 * Hook to implement pull-to-refresh gesture
 *
 * @param config - Pull-to-refresh configuration
 * @returns Pull state and event handlers
 */
export const usePullToRefresh = (config: PullToRefreshConfig): PullToRefreshState => {
  const {
    onRefresh,
    refreshThreshold = 80,
    maxPullDistance = 150,
    requireScrollTop = true,
    disabled = false,
  } = config;

  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const touchStart = useRef<TouchPosition | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  /**
   * Check if container is scrolled to top
   */
  const isAtTop = (element: HTMLElement): boolean => {
    return element.scrollTop === 0;
  };

  /**
   * Handle touch start event
   */
  const handleTouchStart = (e: TouchEvent) => {
    if (disabled || isRefreshing) {
      return;
    }

    const target = e.currentTarget as HTMLElement;
    containerRef.current = target;

    // Only start pull if at top of scroll
    if (requireScrollTop && !isAtTop(target)) {
      return;
    }

    const touch = e.touches[0];
    if (touch) {
      touchStart.current = {
        y: touch.clientY,
        scrollTop: target.scrollTop,
      };
    }
  };

  /**
   * Handle touch move event
   */
  const handleTouchMove = (e: TouchEvent) => {
    if (disabled || isRefreshing || !touchStart.current) {
      return;
    }

    const target = e.currentTarget as HTMLElement;
    const touch = e.touches[0];

    if (!touch) {
      return;
    }

    // Calculate pull distance
    const currentY = touch.clientY;
    const startY = touchStart.current.y;
    const delta = currentY - startY;

    // Only pull down when at top
    if (delta > 0 && target.scrollTop === 0) {
      // Prevent default scroll behavior during pull
      e.preventDefault();

      // Apply resistance curve (harder to pull as distance increases)
      const resistance = 0.5;
      const adjustedDelta = Math.min(delta * resistance, maxPullDistance);

      setIsPulling(true);
      setPullDistance(adjustedDelta);
    } else {
      // Reset pull state if scrolling up or not at top
      setIsPulling(false);
      setPullDistance(0);
    }
  };

  /**
   * Handle touch end event
   */
  const handleTouchEnd = (e: TouchEvent) => {
    if (disabled || isRefreshing || !touchStart.current) {
      return;
    }

    // Check if pull threshold was exceeded
    if (isPulling && pullDistance >= refreshThreshold) {
      // Trigger refresh
      setIsRefreshing(true);

      Promise.resolve(onRefresh())
        .then(() => {
          setIsRefreshing(false);
          setIsPulling(false);
          setPullDistance(0);
        })
        .catch((error) => {
          console.error('[usePullToRefresh] Refresh failed:', error);
          setIsRefreshing(false);
          setIsPulling(false);
          setPullDistance(0);
        });
    } else {
      // Reset pull state (threshold not reached)
      setIsPulling(false);
      setPullDistance(0);
    }

    // Reset touch start
    touchStart.current = null;
  };

  return {
    pullHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isPulling,
    pullDistance,
    isRefreshing,
    canRefresh: pullDistance >= refreshThreshold,
  };
};
